import { useEffect, useState } from "react";
import { useSetTilePresentation, type Tile } from "@/api/queries";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PresentationFields } from "./PresentationFields";
import { draftFromSpec, presentationFrom, type Draft } from "./presentation";

/** How long the form waits after a change before redrawing the preview. */
const SETTLE_MS = 350;

/**
 * Edit chart (TILE-1): redraw a tile from the rows it already has.
 *
 * Nothing runs — the server redraws the stored result — so the preview
 * follows the form as it changes rather than waiting for a button. A table
 * typed into "Add a tile" becomes a chart here, and a chart the model drew
 * gets the band, the highlight or the year switch it should have had.
 */
export function TileChartDialog({
  tile,
  open,
  onClose,
}: {
  tile: Tile;
  open: boolean;
  onClose: () => void;
}) {
  const preview = useSetTilePresentation();
  const save = useSetTilePresentation();
  const [draft, setDraft] = useState<Draft>(() => draftFromSpec(tile.spec));
  const [shown, setShown] = useState<Tile>(tile);

  // Each opening starts from what the tile is now.
  const [seeded, setSeeded] = useState(false);
  if (open && !seeded) {
    setSeeded(true);
    setDraft(draftFromSpec(tile.spec));
    setShown(tile);
  }
  if (!open && seeded) setSeeded(false);

  const { mutate: redraw } = preview;
  useEffect(() => {
    if (!open || !seeded) return;
    const timer = window.setTimeout(() => {
      redraw(
        { tileId: tile.id, presentation: presentationFrom(draft), dryRun: true },
        { onSuccess: setShown },
      );
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, open, seeded, tile.id, redraw]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="Edit chart"
      description="Redraw this tile from the rows it already has — nothing runs again."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { tileId: tile.id, presentation: presentationFrom(draft) },
                { onSuccess: onClose },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <PresentationFields draft={draft} columns={tile.columns ?? []} onChange={setDraft} />
        <ArtifactCard artifact={shown.artifact} csvUrl="" chrome={false} />
      </div>
    </Modal>
  );
}
